import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, Variant } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import DashboardBanner from '../../../shared/components/dashboard-banner/DashboardBanner';
import StatisticsButtonType from '../../../shared/components/dashboard-banner/enums/StatisticsButtonType';
import Dialog from '../../../shared/components/dialog/Dialog';
import Loader from '../../../shared/components/loader/Loader';
import RenderMode from '../../../shared/models/survey/RenderMode';
import SurveyAnswer from '../../../shared/models/survey/SurveyAnswer';
import SurveyQuestionMultipleChoice from '../../../shared/models/survey/SurveyQuestionMultipleChoice';
import SurveyQuestionType from '../../../shared/models/survey/SurveyQuestionType';
import { postAnswers } from '../../survey/data/SurveyService';
import useSurveyContext from '../../survey/store/UseSurveyContext';
import Category from '../components/category/Category';
import useResultContext from '../store/UseResultContext';
import { Result, SurveyQuestionCategory } from '../types';

const DashboardThemePage = () => {
  const { t } = useTranslation();
  const { bemClassName, bemClassNames } = useBem('dashboard');
  const { result, updateResult, lockedCategories } = useResultContext();
  const {
    questionAnswers,
    activationCode,
    setQuestionAnswer,
    resetAnswers,
    setEmailAddress,
    getBackupQuestionAnswers,
    sessionKey,
    survey,
  } = useSurveyContext();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [mail, setMail] = useState<string>('');
  const [showMailDialog, setShowMailDialog] = useState<boolean>(false);
  const { isModified, updateIsModified } = useResultContext();

  if (!survey) {
    return <Loader />;
  }

  const variableQuestions = survey.questions.filter((question) => question.variability === 'Variable');
  const categorizedQuestions: SurveyQuestionCategory[] = [];

  variableQuestions.forEach((question) => {
    const { questionType, group } = question;

    let renderMode = RenderMode.Horizontal;

    if (questionType === SurveyQuestionType.MULTIPLE_CHOICE) {
      const { renderMode: questionRenderMode } = question as SurveyQuestionMultipleChoice;
      if (questionRenderMode === RenderMode.Vertical) {
        renderMode = RenderMode.Vertical;
      }
    }

    const category = categorizedQuestions.find((c) => c.category === group);

    if (category) {
      const { renderMode: categoryRenderMode } = category;

      if (categoryRenderMode === RenderMode.Horizontal && renderMode === RenderMode.Vertical) {
        category.renderMode = RenderMode.Vertical;
      }

      category.questions.push(question);
    } else {
      categorizedQuestions.push({
        category: question.group,
        questions: [question],
        renderMode,
      });
    }
  });

  const improvementCategories = categorizedQuestions
    .filter((c) => lockedCategories.improvements.includes(c.category))
    .sort((a, b) => {
      const indexA = lockedCategories.improvements.indexOf(a.category);
      const indexB = lockedCategories.improvements.indexOf(b.category);
      return indexA - indexB;
    });

  const otherCategories = categorizedQuestions.filter((c) => !lockedCategories.improvements.includes(c.category));

  const toggleDialog = () => {
    setShowDialog(!showDialog);
  };

  const toggleMailDialog = () => {
    setShowMailDialog(!showMailDialog);
  };

  const submitMail = () => {
    setEmailAddress(mail);
    const answers = getBackupQuestionAnswers();
    postAnswers(answers, activationCode, true, mail, sessionKey).then(() => {
      toggleMailDialog();
    });
  };

  const recalculate = () => {
    postAnswers(questionAnswers, activationCode, false).then((postResult: any) => {
      const { data: postResultData } = postResult;
      updateResult(postResultData as Result);
    });
    updateIsModified(true);
  };

  const toggleReset = () => {
    resetAnswers();
    const answers = getBackupQuestionAnswers();
    postAnswers(answers, activationCode, false).then((postResult: any) => {
      const { data: postResultData } = postResult;
      updateResult(postResultData as Result);
    });
    updateIsModified(false);
  };

  const onSetNewAnswer = (questionAnswer: SurveyAnswer) => {
    setQuestionAnswer(questionAnswer);
    recalculate();
  };

  return (
    <div className={bemClassNames([bemClassName(), 'background-image'])}>
      <DashboardBanner
        inputAge={result.inputLeeftijd ?? 0}
        realAge={result.echteLeeftijd ?? 0}
        statisticsButtonType={StatisticsButtonType.Reset}
        onStatisticsButtonClick={toggleReset}
        title={t('dashboard.subtitle')}
        paragraph={t('dashboard.paragraph')}
        hasChanges={isModified}
      />

      <div className={bemClassName('habits-list')}>
        {improvementCategories.map((category) => (
          <Category
            key={category.category}
            category={category}
            setQuestionAnswer={onSetNewAnswer}
            showNotes
          />
        ))}
        {otherCategories.map((category) => (
          <Category
            key={category.category}
            category={category}
            setQuestionAnswer={onSetNewAnswer}
            showNotes
          />
        ))}
      </div>

      {showDialog && (
        <Dialog
          onClose={toggleDialog}
          title={t('dashboard.dialog.title')!}
          content={
            <Trans
              i18nKey="dashboard.dialog.content"
              components={{ 1: <br /> }}
            />
          }
        />
      )}

      {showMailDialog && (
        <Dialog
          onClose={toggleMailDialog}
          title={t('dashboard.mail-dialog.title')!}
          content={
            <>
              <Trans
                i18nKey="dashboard.mail-dialog.content"
                components={{ 1: <br /> }}
              />
              <div className="mail-dialog__input">
                <Input
                  type="text"
                  value={mail}
                  placeholder={t('dashboard.mail-dialog.input')!}
                  onChange={(e: InputChangeEvent) => {
                    const {
                      target: { value },
                    } = e;
                    setMail(value as string);
                  }}
                />
              </div>
            </>
          }
          actions={
            <>
              <Button
                onClick={toggleMailDialog}
                variant={Variant.Plain}
              >
                {t('dashboard.mail-dialog.cancel')}
              </Button>
              <Button
                onClick={submitMail}
                variant={Variant.Plain}
              >
                {t('dashboard.mail-dialog.submit')!}
              </Button>
            </>
          }
        />
      )}
    </div>
  );
};

export default DashboardThemePage;
